import * as turf from '@turf/turf'
import { useFlightRouteModalState } from '../piniastore/flightRouteModalState'

class RobustAutoGridGenerator {
  constructor () {
    // Constants for default behavior
    this.IMAGE_INTERVAL = 2 // seconds, base interval if needed
    this.MIN_TURN_RADIUS = 50 // meters minimum turning radius
    this.MIN_AREA_THRESHOLD = 100 // m² minimal AOI size warning
  }

  generateFlightPlan (polygon, aircraft, payload, missionParams, dem = null) {
    try {
      // Validate AOI early
      this.validateAOI(polygon)

      // Step 1: Calculate flight parameters using dynamic FOV
      const flightParams = this.calculateFlightParameters(
        payload,
        missionParams.altitude,
        missionParams.forwardOverlap,
        missionParams.sideOverlap,
        missionParams.gsd,
        missionParams.speed
      )

      // Step 2: Validate parameters against aircraft capabilities
      const issues = this.validateAircraftCompatibility(
        flightParams,
        aircraft,
        missionParams
      )

      // Step 3: Generate optimal grid pattern
      const gridPattern = this.generateOptimizedGrid(polygon, {
        ...flightParams,
        courseAngle: missionParams.courseAngle,
      })

      // Step 4: Create waypoints with terrain-aware altitudes
      const waypoints = this.createWaypointsWithActions(
        gridPattern,
        flightParams,
        payload,
        dem,
        missionParams.altitude,
        missionParams.speed
      )

      const waypointsWithDampingDist = []

      for (let i = 0; i < waypoints.length; i++) {
        if (i === 0 || i === waypoints.length - 1) {
          waypointsWithDampingDist.push(waypoints[i])
        } else {
          const dampingDist = this.calculateDampingDistance(waypoints[i - 1].position.geometry.coordinates, waypoints[i].position.geometry.coordinates, waypoints[i + 1].position.geometry.coordinates, missionParams.speed)
          const point = { ...waypoints[i], dampingDistance: dampingDist }
        }
      }

      // Step 5: Insert turn radius waypoints for smooth transitions
      // const waypointsWithTurns = this.insertTurnRadiusWaypoints(
      //   waypoints,
      //   this.MIN_TURN_RADIUS
      // );

      // Step 6: Calculate mission statistics with corrected coverage area
      const statistics = this.calculateMissionStatistics(
        waypoints,
        flightParams,
        aircraft
      )

      // Step 7: Generate flight plan object
      const flightPlan = {
        metadata: {
          generated: new Date().toISOString(),
          algorithm: 'hellogeo-enhanced',
          version: '3.0',
        },
        aircraft,
        payload,
        parameters: flightParams,
        flightLines: gridPattern.flightLines,
        polygon,
        waypoints: waypoints,
        statistics,
        validation: this.validateFlightPlan(
          waypoints,
          flightParams,
          aircraft,
          issues
        ),
      }

      return flightPlan
    } catch (error) {
      throw new Error(`Flight plan generation failed: ${error.message}`)
    }
  }

  validateAOI (polygon) {
    const area = turf.area(polygon)
    if (area < this.MIN_AREA_THRESHOLD) {
      console.warn(
        `AOI area is very small (${area.toFixed(
          2
        )} m²). Flight planning may be inaccurate.`
      )
    }
    if (!turf.booleanValid(polygon)) {
      throw new Error('Invalid AOI polygon geometry.')
    }
  }

  calculateFlightParameters (
    payload,
    agl,
    forwardOverlap,
    sideOverlap,
    targetGSD,
    speed
  ) {
    payload.sensorHeight =
      payload.sensorHeight || (payload.sensorWidth * 2) / 3

    const verticalFOV =
      2 * Math.atan(payload.sensorHeight / 2 / payload.focalLength)
    const horizontalFOV =
      2 * Math.atan(payload.sensorWidth / 2 / payload.focalLength)

    // Calculate Ground Sample Distance (GSD) in cm/px
    const gsd = targetGSD || this.calculateGSD(agl, payload)

    // Calculate photo footprint at altitude
    const forwardPhotoHeight = 2 * agl * Math.tan(verticalFOV / 2) // meters
    const sidePhotoWidth = 2 * agl * Math.tan(horizontalFOV / 2) // meters

    // Overlap distances
    const forwardOverlapDistance = forwardPhotoHeight * (forwardOverlap / 100)
    const sideOverlapDistance = sidePhotoWidth * (sideOverlap / 100)

    // Effective distances after overlap
    const effectiveForwardDistance =
      forwardPhotoHeight - forwardOverlapDistance
    const effectiveSideDistance = sidePhotoWidth - sideOverlapDistance

    // Line spacing between flight lines
    const lineSpacing = effectiveSideDistance

    // Calculate optimal speed to maintain photo spacing and forward overlap
    // Use formula: photo_interval = photo_spacing / speed, rearranged for speed:
    // speed = effectiveForwardDistance / desiredPhotoInterval
    // Here, set desired photo interval to IMAGE_INTERVAL (2s) minimum
    const photoInterval = this.calculatePhotoInterval(effectiveForwardDistance, speed)
    const optimalSpeed = this.calculateOptimalSpeed(
      effectiveForwardDistance,
      photoInterval
    )

    return {
      gsd,
      altitude: agl,
      forwardPhotoHeight,
      sidePhotoWidth,
      forwardOverlapDistance,
      sideOverlapDistance,
      effectiveForwardDistance,
      effectiveSideDistance,
      lineSpacing,
      photoInterval,
      optimalSpeed,
      forwardOverlap,
      sideOverlap,
      verticalFOV,
      horizontalFOV,
    }
  }

  calculateGSD (altitude, payload) {
    // GSD = (Altitude * sensor_width) / (focal_length * image_width)
    // Result in meters per pixel, convert to cm per pixel
    const gsdMeters =
      (altitude * payload.sensorWidth) /
      (payload.focalLength * payload.imageWidth)
    return gsdMeters * 100
  }

  calculatePhotoInterval (effectiveForwardDistance, desiredSpeed = 6) {
    // desiredSpeed can be adjusted or passed dynamically if needed
    const interval = effectiveForwardDistance / desiredSpeed

    // Clamp interval between 1 and 10 seconds for safety
    return Math.max(1, Math.min(interval, 10))
  }

  calculateOptimalSpeed (effectiveForwardDistance, photoInterval) {
    // Speed = spacing / interval, constrain to 3-12 m/s range
    const speed = effectiveForwardDistance / photoInterval
    return Math.min(Math.max(speed, 3), 12)
  }

  validateAircraftCompatibility (flightParams, aircraft, missionParams) {
    const issues = []

    if (flightParams.optimalSpeed > aircraft.maxSpeed) {
      issues.push(
        `Optimal speed ${flightParams.optimalSpeed.toFixed(
          2
        )}m/s exceeds aircraft maximum ${aircraft.maxSpeed}m/s`
      )
    }

    if (missionParams.altitude > aircraft.maxAltitude) {
      issues.push(
        `Flight altitude ${missionParams.altitude}m exceeds aircraft maximum ${aircraft.maxAltitude}m`
      )
    }

    if (flightParams.gsd > 10) {
      issues.push(
        `GSD ${flightParams.gsd}cm may be too low for detailed mapping`
      )
    }

    if (issues.length > 0) {
      console.warn('Aircraft compatibility issues:', issues)
    }

    return issues
  }

  sortPointsAlongLine (points, line) {
    const lineCoords = line.geometry.coordinates
    const lineStart = turf.point(lineCoords[0])

    return points.sort((a, b) => {
      const distA = turf.distance(lineStart, a)
      const distB = turf.distance(lineStart, b)
      return distA - distB
    })
  }

  generateOptimizedGrid (polygon, flightParams) {
    const flightRouteState = useFlightRouteModalState()
    // Find optimal flight line orientation
    let optimalAngle

    if (flightParams.courseAngle) {
      optimalAngle = flightParams.courseAngle
    } else {
      optimalAngle = this.findOptimalFlightLineAngle(polygon)
    }
    // const optimalAngle = 290
    flightRouteState.orthoSettings.courseAngle = optimalAngle

    const centroid = turf.centroid(polygon)
    const rotatedPolygon = turf.transformRotate(polygon, -optimalAngle, {
      pivot: centroid.geometry.coordinates,
    })

    const bbox = turf.bbox(rotatedPolygon)
    const flightLines = this.generateParallelFlightLines(
      bbox,
      flightParams.lineSpacing
    )

    const clippedLines = this.clipFlightLinesToPolygon(
      flightLines,
      rotatedPolygon
    )

    const optimizedLines = this.optimizeLineSequence(clippedLines)

    // Rotate lines back to original orientation
    const finalLines = optimizedLines.map((line) =>
      turf.transformRotate(line, optimalAngle, {
        pivot: centroid.geometry.coordinates,
      })
    )

    return {
      flightLines: finalLines,
      optimalAngle,
      boundingBox: bbox,
      totalLines: finalLines.length,
    }
  }

  findOptimalFlightLineAngle (polygon) {
    let minArea = Infinity
    let optimalAngle = 0

    for (let angle = 0; angle < 180; angle += 5) {
      const rotated = turf.transformRotate(polygon, angle)
      const bbox = turf.bbox(rotated)
      const area = (bbox[2] - bbox[0]) * (bbox[3] - bbox[1])
      if (area < minArea) {
        minArea = area
        optimalAngle = angle
      }
    }
    return optimalAngle
  }

  generateParallelFlightLines (bbox, lineSpacing) {
    const [minX, minY, maxX, maxY] = bbox
    const lines = []

    // Convert meters to degrees (latitude approx)
    const metersToDegreesLat = (meters) => meters / 111320
    const lineSpacingDeg = metersToDegreesLat(lineSpacing)

    let currentY = minY + lineSpacingDeg / 2

    while (currentY <= maxY) {
      const line = turf.lineString([
        [minX - 0.01, currentY],
        [maxX + 0.01, currentY],
      ])
      lines.push(line)
      currentY += lineSpacingDeg
    }
    return lines
  }

  clipFlightLinesToPolygon (flightLines, polygon) {
    const clippedLines = []

    flightLines.forEach((line) => {
      try {
        // Find intersection points
        const intersections = turf.lineIntersect(line, polygon)

        if (intersections.features.length >= 2) {
          // Sort intersection points along the line
          const sortedPoints = this.sortPointsAlongLine(
            intersections.features,
            line
          )

          // Create line segments between pairs of intersection points
          for (let i = 0; i < sortedPoints.length; i += 2) {
            if (i + 1 < sortedPoints.length) {
              const segment = turf.lineString([
                sortedPoints[i].geometry.coordinates,
                sortedPoints[i + 1].geometry.coordinates,
              ])

              // Verify segment is inside polygon
              const midpoint = turf.midpoint(
                sortedPoints[i].geometry.coordinates,
                sortedPoints[i + 1].geometry.coordinates
              )

              if (turf.booleanPointInPolygon(midpoint, polygon)) {
                clippedLines.push(segment)
              }
            }
          }
        }
      } catch (error) {
        console.warn('Error clipping line to polygon:', error)
      }
    })

    return clippedLines
  }

  optimizeLineSequence (lines) {
    if (lines.length <= 1) return lines

    const optimized = [lines[0]]
    const remaining = [...lines.slice(1)]

    while (remaining.length > 0) {
      const lastLine = optimized[optimized.length - 1]
      const lastEnd =
        lastLine.geometry.coordinates[lastLine.geometry.coordinates.length - 1]

      let closestIndex = 0
      let minDistance = Infinity
      let shouldReverse = false

      remaining.forEach((line, index) => {
        const lineStart = line.geometry.coordinates[0]
        const lineEnd =
          line.geometry.coordinates[line.geometry.coordinates.length - 1]

        const distToStart = turf.distance(
          turf.point(lastEnd),
          turf.point(lineStart)
        )
        const distToEnd = turf.distance(
          turf.point(lastEnd),
          turf.point(lineEnd)
        )

        if (distToStart < minDistance) {
          minDistance = distToStart
          closestIndex = index
          shouldReverse = false
        }
        if (distToEnd < minDistance) {
          minDistance = distToEnd
          closestIndex = index
          shouldReverse = true
        }
      })

      let nextLine = remaining[closestIndex]
      if (shouldReverse) {
        const coords = [...nextLine.geometry.coordinates].reverse()
        nextLine = turf.lineString(coords)
      }

      optimized.push(nextLine)
      remaining.splice(closestIndex, 1)
    }
    return optimized
  }

  createWaypointsWithActions (
    gridPattern,
    flightParams,
    payload,
    dem,
    baseAltitude,
    speed
  ) {
    const waypoints = []
    let waypointIndex = 0

    gridPattern.flightLines.forEach((line, lineIndex) => {
      const coords = line.geometry.coordinates

      coords.forEach((coord, coordIndex) => {
        // Adjust altitude using DEM if available

        const prevCoord = coordIndex > 0 ? coords[coordIndex - 1] : null
        const nextCoord =
          coordIndex < coords.length - 1 ? coords[coordIndex + 1] : null

        let altitude = baseAltitude
        if (dem) {
          const groundElevation = this.getElevationFromDEM(coord, dem)
          altitude = groundElevation + baseAltitude
        }

        // const dampingDistance = this.calculateDampingDistance(
        //   prevCoord,
        //   coord,
        //   nextCoord,
        //   speed
        // )

        const waypoint = {
          index: waypointIndex++,
          position: {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: coord,
            },
            properties: {},
          },
          altitude,
          speed: flightParams.optimalSpeed,
          actions: this.generateWaypointActions(
            coordIndex,
            coords.length,
            lineIndex,
            flightParams,
            payload
          ),
          metadata: {
            lineIndex,
            positionInLine: coordIndex,
            isLineStart: coordIndex === 0,
            isLineEnd: coordIndex === coords.length - 1,
            isLastLine: lineIndex === gridPattern.flightLines.length - 1,
          },
        }
        waypoints.push(waypoint)
      })
    })

    return waypoints
  }

  getElevationFromDEM (coord, dem) {
    // Assuming DEM is a raster grid or function: DEM.getElevation(lon, lat)
    // Here we mock as zero if not available
    if (typeof dem.getElevation === 'function') {
      return dem.getElevation(coord[0], coord[1])
    }
    return 0
  }

  generateWaypointActions (
    coordIndex,
    coordsLength,
    lineIndex,
    flightParams,
    payload
  ) {
    const actions = []

    // Take photo at intervals according to photoInterval
    if (coordIndex === 0 || coordIndex === coordsLength - 1) {
      actions.push({
        type: 'TAKE_PHOTO',
        payload,
      })
    }

    // Could add gimbal control or other payload actions here

    return actions
  }

  calculateDampingDistance (prevCoord, currCoord, nextCoord, speed) {
    const minDamping = 5 // meters
    const k = 1.5

    if (!prevCoord || !nextCoord) return minDamping

    const angle = this.calculateTurnAngle(prevCoord, currCoord, nextCoord)

    const turnSharpness = 180 - Math.abs(angle)
    const damping = k * (turnSharpness / 180) * parseFloat(speed)
    return Math.max(minDamping, damping)
  }

  insertTurnRadiusWaypoints (waypoints, minTurnRadius) {
    if (waypoints.length < 3) return waypoints

    const newWaypoints = [waypoints[0]]

    for (let i = 1; i < waypoints.length - 1; i++) {
      const prev = waypoints[i - 1]
      const current = waypoints[i]
      const next = waypoints[i + 1]

      const angle = this.calculateTurnAngle(
        prev.position.geometry.coordinates,
        current.position.geometry.coordinates,
        next.position.geometry.coordinates
      )

      // If turn angle is sharp, insert intermediate waypoints to smooth turn
      if (Math.abs(angle) < 150) {
        const turnPoints = this.createTurnWaypoints(
          prev,
          current,
          next,
          minTurnRadius
        )
        newWaypoints.push(...turnPoints)
      } else {
        newWaypoints.push(current)
      }
    }

    newWaypoints.push(waypoints[waypoints.length - 1])
    return newWaypoints
  }

  calculateTurnAngle (p1, p2, p3) {
    // Calculate angle at p2 formed by segments p1-p2 and p2-p3
    const bearing1 = turf.bearing(turf.point(p2), turf.point(p1))
    const bearing2 = turf.bearing(turf.point(p2), turf.point(p3))

    let angle = bearing2 - bearing1
    if (angle > 180) angle -= 360
    if (angle < -180) angle += 360

    return angle
  }

  createTurnWaypoints (prev, current, next, minTurnRadius) {
    // Insert 1-2 waypoints on turn arc to respect min turn radius
    // This is a simplified example for smooth curve

    const p1 = turf.point(prev.position.geometry.coordinates)
    const p2 = turf.point(current.position.geometry.coordinates)
    // const p3 = turf.point(next.position.geometry.coordinates);

    const angle = this.calculateTurnAngle(
      prev.position.geometry.coordinates,
      current.position.geometry.coordinates,
      next.position.geometry.coordinates
    )

    // const turnDirection = angle > 0 ? 1 : -1; // 1 for left turn, -1 for right

    // const distanceP1P2 = turf.distance(p1, p2, { units: "meters" });
    // const distanceP2P3 = turf.distance(p2, p3, { units: "meters" });

    // Calculate intermediate points on arc approximating turn radius
    // We'll create two points at quarter and half turn angles

    const numPoints = 2
    const newWaypoints = []

    for (let i = 1; i <= numPoints; i++) {
      const fraction = i / (numPoints + 1)
      // Calculate an intermediate bearing rotated by fraction * angle
      const bearingStart = turf.bearing(p1, p2)
      const bearingEnd = bearingStart + fraction * angle

      // Calculate point distance along turn radius arc from p2
      const arcDistance = minTurnRadius * fraction

      const interpolatedPoint = turf.destination(p2, arcDistance, bearingEnd, {
        units: 'meters',
      })

      newWaypoints.push({
        index: null, // Will be assigned later if needed
        position: interpolatedPoint,
        altitude: current.altitude,
        speed: current.speed,
        actions: [],
        metadata: {
          isTurnWaypoint: true,
        },
      })
    }

    return newWaypoints
  }

  calculateMissionStatistics (waypoints, flightParams, aircraft) {
    // if (waypoints.length === 0) {
    //   return this.getEmptyStatistics();
    // }

    let totalDistance = 0

    for (let i = 1; i < waypoints.length; i++) {
      const prev = turf.point(waypoints[i - 1].position.geometry.coordinates)
      const curr = turf.point(waypoints[i].position.geometry.coordinates)
      totalDistance += turf.distance(prev, curr, { units: 'meters' })
    }

    // Flight time in seconds
    const flightTimeSeconds = totalDistance / flightParams.optimalSpeed

    // Photos based on time interval (photoInterval in seconds)
    const photoCount_time = Math.floor(
      flightTimeSeconds / flightParams.photoInterval
    )

    // Photos based on distance interval (effectiveForwardDistance in meters)
    const photoCount_distance = Math.floor(
      totalDistance / flightParams.effectiveForwardDistance
    )

    // You can choose which method you want to use in your plan.
    // For statistics, returning both:
    const photoCount = photoCount_distance // or photoCount_time, depending on your logic

    // Calculate flight time in minutes for stats
    const flightTimeMinutes = flightTimeSeconds / 60

    // Battery usage %
    const batteryUsage = (flightTimeMinutes / aircraft.batteryLife) * 100

    // Coverage area in hectares (factoring in overlap)
    const coverageArea =
      (flightParams.forwardPhotoHeight *
        flightParams.sidePhotoWidth *
        photoCount *
        (1 - flightParams.forwardOverlap / 100) *
        (1 - flightParams.sideOverlap / 100)) /
      10000

    return {
      totalWaypoints: waypoints.length,
      totalDistance: totalDistance / 1000, // km
      estimatedFlightTime: flightTimeMinutes,
      estimatedBatteryUsage: batteryUsage,
      photoCount_time: photoCount_time,
      photoCount_distance: photoCount_distance,
      photoCount: photoCount,
      coverageArea: coverageArea,
      averageGSD: flightParams.gsd,
      flightLines:
        Math.max(...waypoints.map((wp) => wp.metadata.lineIndex)) + 1,
      optimalSpeed: flightParams.optimalSpeed,
      lineSpacing: flightParams.lineSpacing,
      forwardOverlap: flightParams.forwardOverlap,
      sideOverlap: flightParams.sideOverlap,
    }
  }

  validateFlightPlan (waypoints, flightParams, aircraft, previousIssues) {
    const issues = [...previousIssues]

    if (waypoints.length === 0) {
      issues.push('No waypoints generated.')
    }

    if (flightParams.optimalSpeed < aircraft.minSpeed) {
      issues.push(
        `Calculated speed (${flightParams.optimalSpeed.toFixed(
          2
        )}m/s) is below aircraft minimum speed (${aircraft.minSpeed}m/s)`
      )
    }

    return issues
  }
}

export { RobustAutoGridGenerator }
